export const REQUEST_TIMEOUT_CODE = 'REQUEST_TIMEOUT';

export class RequestTimeoutError extends Error {
    constructor(message, { timeoutMs, method, url } = {}) {
        super(message);
        this.name = 'RequestTimeoutError';
        this.code = REQUEST_TIMEOUT_CODE;
        this.timeoutMs = timeoutMs;
        this.method = method;
        this.url = url;
    }
}

export const createRequestTimeoutError = ({ timeoutMs, method, url } = {}) => (
    new RequestTimeoutError(
        `Request timed out after ${timeoutMs}ms`,
        { timeoutMs, method, url },
    )
);

export const withRequestTimeout = ({ signal, timeoutMs, method, url } = {}) => {
    if (typeof AbortController !== 'function' || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
        return {
            signal,
            cleanup: () => {},
            getTimeoutError: () => null,
        };
    }

    const controller = new AbortController();
    let timeoutError = null;
    let timeoutId = null;

    const abortWithReason = (reason) => {
        if (controller.signal.aborted) {
            return;
        }
        try {
            controller.abort(reason);
        } catch (_) {
            controller.abort();
        }
    };

    const onCallerAbort = () => {
        abortWithReason(signal?.reason);
    };

    if (signal) {
        if (signal.aborted) {
            abortWithReason(signal.reason);
        } else if (typeof signal.addEventListener === 'function') {
            signal.addEventListener('abort', onCallerAbort, { once: true });
        }
    }

    timeoutId = setTimeout(() => {
        timeoutError = createRequestTimeoutError({ timeoutMs, method, url });
        abortWithReason(timeoutError);
    }, timeoutMs);

    return {
        signal: controller.signal,
        cleanup: () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (signal && typeof signal.removeEventListener === 'function') {
                signal.removeEventListener('abort', onCallerAbort);
            }
        },
        getTimeoutError: () => timeoutError,
    };
};
